import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import axios from 'axios';
import { Box, Typography, CircularProgress } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default function InventoryGraph({ productId, rangeStart }) {
  const [inventoryData, setInventoryData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    const fetchInventory = axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory/${productId}`);
    const fetchSales = axios.get(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/sales-since-august-11`, { params: { productId } });

    Promise.all([fetchInventory, fetchSales])
      .then(([invRes, salesRes]) => {
        setInventoryData(invRes.data);
        const salesMap = new Map();
        salesRes.data.forEach(order => {
          order.line_items.forEach(item => {
            if (item.product_id.toString() === productId) {
              const date = new Date(order.processed_at).toISOString().split('T')[0];
              salesMap.set(date, (salesMap.get(date) || 0) + item.quantity);
            }
          });
        });
        const startDate = new Date('2024-08-11');
        const endDate = new Date();
        const dates = [];
        let cursor = new Date(startDate);
        while (cursor <= endDate) {
          dates.push(cursor.toISOString().split('T')[0]);
          cursor.setDate(cursor.getDate() + 1);
        }
        const finalSales = dates.map(date => ({ date, quantity: salesMap.get(date) || 0 }));
        setSalesData(finalSales);
      })
      .catch(error => console.error('Fetching error:', error))
      .finally(() => setLoading(false));
  }, [productId]);

  const merged = inventoryData.map(entry => {
    const dateKey = entry.date.split('T')[0];
    const sale = salesData.find(s => s.date === dateKey);
    return { date: dateKey, inventory: entry.quantity, sales: sale ? sale.quantity : 0 };
  });

  const mismatches = merged.map((e, i, arr) => {
    const prev = i > 0 ? arr[i - 1].inventory : e.inventory;
    const next = i < arr.length - 1 ? arr[i + 1].inventory : e.inventory;
    const changePrev = e.inventory - prev;
    const changeNext = next - e.inventory;
    const mismatch = changePrev !== -e.sales && changeNext !== -e.sales;
    return { ...e, mismatch };
  });

  const effectiveStart = rangeStart || new Date(0);
  const displayData = mismatches.filter(d => new Date(d.date) >= effectiveStart);

  const labels = displayData.map(d => d.date);
  const data = {
    labels,
    datasets: [{
      label: 'Inventory Levels',
      data: displayData.map(d => d.inventory),
      fill: false,
      tension: 0.1,
      borderWidth: 2,
      pointHitRadius: 5,
      pointHoverRadius: 5,
      borderColor: ctx => {
        const idx = ctx.p2DataIndex;
        return idx != null && displayData[idx].mismatch ? '#ef4444' : '#2383e2';
      },
      pointBackgroundColor: displayData.map(d => (d.mismatch ? '#ef4444' : '#2383e2')),
      segment: {
        borderColor: ctx => {
          const idx = ctx.p2DataIndex;
          return idx != null && displayData[idx].mismatch ? '#ef4444' : '#2383e2';
        },
      },
    }],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'top', labels: { font: { family: 'Inter', size: 12 }, usePointStyle: true, pointStyleWidth: 8 } },
      tooltip: {
        mode: 'index', intersect: false,
        titleFont: { family: 'Inter' }, bodyFont: { family: 'Inter' },
        callbacks: {
          title: ctx => displayData[ctx[0].dataIndex]?.date || 'Date N/A',
          label: ctx => `Inventory: ${ctx.raw}`,
        },
      },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { beginAtZero: true, ticks: { font: { family: 'Inter', size: 10 } }, grid: { color: 'rgba(0,0,0,0.04)' } },
      y: { beginAtZero: true, title: { display: true, text: 'Inventory Level', font: { family: 'Inter', size: 11 } }, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { family: 'Inter', size: 10 } } },
    },
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress size={28} thickness={3} />
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Inventory Levels</Typography>
          <Line data={data} options={options} />
        </>
      )}
    </Box>
  );
}
